import { useEffect, useRef } from "react";
import { s } from "../visual.jsx";

/**
 * A onda do que o microfone está ouvindo AGORA.
 *
 * Existe por um motivo prático, não decorativo: a barra vermelha com o
 * cronômetro andando também aparece quando o microfone está mudo — mudo por
 * seleção do dispositivo errado, por hardware com tecla de mute, por permissão
 * dada a outra aba. Sem ver a voz mexer, a pessoa fala dois minutos e manda
 * silêncio para o cliente.
 *
 * A altura das barras é escrita DIRETO no DOM, dentro do `requestAnimationFrame`:
 * 60 quadros por segundo em `useState` re-renderizariam o composer inteiro (e,
 * pelo contexto, meia mesa) sessenta vezes por segundo para animar oito
 * retângulos.
 *
 * Aqui `s()` continua sendo a regra e não a exceção da migração: cor, altura,
 * largura e folga chegam por prop e são calculadas em execução — não há classe
 * do Tailwind para um número que só existe na chamada.
 */
export default function OndaDoRecado({
  trilha,
  cor = "var(--color-terra)",
  quantas = 9,
  altura = 22,
  largura = 2.5,
  folga = 2.5,
}) {
  const barras = useRef([]);
  const BARRAS = quantas;

  useEffect(() => {
    if (!trilha || typeof AudioContext === "undefined") return;
    const audio = new AudioContext();
    const analisador = audio.createAnalyser();
    /* Janela curta: o que interessa é o volume instantâneo da voz, não o
       espectro. 256 amostras dão resposta imediata e custam quase nada. */
    analisador.fftSize = 256;
    const fonte = audio.createMediaStreamSource(trilha);
    fonte.connect(analisador);
    const amostras = new Uint8Array(analisador.frequencyBinCount);
    let quadro = 0;

    const desenhar = () => {
      analisador.getByteTimeDomainData(amostras);
      /* Pico da janela, não média: a média de uma senoide fica sempre perto de
         zero e a barra não mexia. */
      let pico = 0;
      for (const v of amostras) pico = Math.max(pico, Math.abs(v - 128));
      const nivel = Math.min(1, pico / 90);
      for (const [i, el] of barras.current.entries()) {
        if (!el) continue;
        /* As do meio mexem mais: dá a forma de onda em vez de nove barras
           idênticas subindo juntas. */
        const peso = 0.45 + 0.55 * Math.sin((Math.PI * (i + 1)) / (BARRAS + 1));
        /* `scaleY`, não `height`: altura muda o layout, e mexer no layout de nove
           barras sessenta vezes por segundo faz o navegador recalcular a linha
           inteira do compositor a cada quadro. A escala anda na composição. */
        const alto = Math.max(3, 3 + nivel * peso * (altura - 3));
        el.style.transform = `scaleY(${(alto / altura).toFixed(3)})`;
      }
      quadro = requestAnimationFrame(desenhar);
    };
    quadro = requestAnimationFrame(desenhar);

    return () => {
      cancelAnimationFrame(quadro);
      fonte.disconnect();
      /* Fechar o contexto: cada gravação abria um novo e o navegador limita
         quantos existem por aba — depois de alguns recados, nenhum abria mais. */
      void audio.close().catch(() => {});
    };
  }, [trilha, BARRAS, altura]);

  return (
    <span
      aria-hidden="true"
      className="flex min-w-0 flex-1 items-center justify-center"
      style={s(`gap:${folga}px;height:${altura}px`)}
    >
      {Array.from({ length: BARRAS }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            barras.current[i] = el;
          }}
          /* A barra nasce com a altura CHEIA e comprimida por `scaleY` — é o que
             deixa o quadro da onda ser só composição. Começa no mínimo audível. */
          className="transition-transform duration-[60ms] ease-linear"
          style={s(
            `width:${largura}px;height:${altura}px;border-radius:${largura}px;background:${cor};transform:scaleY(${(3 / altura).toFixed(3)})`,
          )}
        />
      ))}
    </span>
  );
}
